import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reconciliationApi } from '../lib/api';
import { formatDate, formatCurrency } from '../utils/format';
import { AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';

export default function Reconciliation() {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showMatched, setShowMatched] = useState(false);

  const { data: summaryData } = useQuery({
    queryKey: ['reconciliation-summary', dateFrom, dateTo],
    queryFn: () =>
      reconciliationApi.getSummary({
        from: dateFrom || undefined,
        to: dateTo || undefined,
      }),
    enabled: !!dateFrom && !!dateTo,
  });

  const { data: missingData, isLoading } = useQuery({
    queryKey: ['missing-receipts', dateFrom, dateTo],
    queryFn: () =>
      reconciliationApi.getMissing({
        from: dateFrom || undefined,
        to: dateTo || undefined,
      }),
    enabled: !!dateFrom && !!dateTo,
  });

  const summary = summaryData?.data?.summary;
  const missingReceipts = missingData?.data?.missingReceipts || [];

  const handleAutoMatch = async () => {
    if (!dateFrom || !dateTo) {
      toast.error('Please select date range');
      return;
    }

    try {
      const response = await reconciliationApi.autoMatch(dateFrom, dateTo, 0.85);
      toast.success(
        `Found ${response.data.matchedCount} potential matches out of ${response.data.totalTransactions} transactions`
      );
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Auto-match failed');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Reconciliation</h1>
        <p className="text-gray-600">
          Match receipts with bank transactions and identify missing receipts
        </p>
      </div>

      {/* Date Range Selector */}
      <div className="card">
        <div className="flex items-center gap-4">
          <div>
            <label className="label">From Date</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="input"
            />
          </div>
          <div>
            <label className="label">To Date</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="input"
            />
          </div>
          <div className="flex-1" />
          <button onClick={handleAutoMatch} className="btn-primary mt-6">
            Auto-Match Transactions
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Transactions</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {summary.total.count}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {formatCurrency(summary.total.amount)}
                </p>
              </div>
              <AlertCircle className="h-8 w-8 text-gray-400" />
            </div>
          </div>

          <div className="card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-600">Matched</p>
                <p className="text-3xl font-bold text-green-600 mt-2">
                  {summary.matched.count}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {summary.matched.percentage.toFixed(1)}% •{' '}
                  {formatCurrency(summary.matched.amount)}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </div>

          <div className="card">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-600">Missing Receipts</p>
                <p className="text-3xl font-bold text-red-600 mt-2">
                  {summary.unmatched.count}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {summary.unmatched.percentage.toFixed(1)}% •{' '}
                  {formatCurrency(summary.unmatched.amount)}
                </p>
              </div>
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
          </div>
        </div>
      )}

      {/* Missing Receipts Table */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Missing Receipts ({missingReceipts.length})
          </h2>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showMatched}
              onChange={(e) => setShowMatched(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm text-gray-700">Show matched</span>
          </label>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : missingReceipts.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <p className="text-gray-500">All transactions matched!</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Account
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {missingReceipts.map((txn: any) => (
                  <tr key={txn.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(txn.transaction_date)}
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-900">{txn.description}</p>
                      {txn.merchant && (
                        <p className="text-xs text-gray-500">{txn.merchant}</p>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(Math.abs(txn.amount))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {txn.account_type} •••{txn.account_last_four}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <button className="text-primary-600 hover:text-primary-900">
                        Find Receipt
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="card bg-blue-50 border-blue-200">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">
          How Reconciliation Works
        </h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>1. Upload your bank or credit card statement</li>
          <li>2. Select a date range to reconcile</li>
          <li>3. Click "Auto-Match Transactions" to automatically match receipts</li>
          <li>4. Review unmatched transactions and manually find receipts</li>
          <li>5. Export missing receipts list for follow-up</li>
        </ul>
      </div>
    </div>
  );
}

